import { ValidationUtils } from '@admin/shared/validation/validation-runner';
import { IValidator } from '@admin/shared/validation/validators';
import {
  AccountModel,
  InvoiceCoverageModel,
  InvoiceModel,
  PaymentModel,
} from '../shared/models/payment-model';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { PaymentService } from '@admin/billing/payments/shared/services/payment.service';
import { FieldState } from '@admin/shared/models/field-state';
import { Validator } from '@admin/shared/validation/validators';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'hr-edit-payment',
  templateUrl: './edit-payment.component.html',
  styleUrls: ['./edit-payment.component.less'],
  standalone: false,
})
export class EditPaymentComponent implements OnInit {
  constructor(
    private paymentService: PaymentService,
    private translationService: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['payment'] && changes['payment'].currentValue != null) {
      if (this.mode.edit && !this.payment.payerAccount.accountId)
        this.filterAccount(
          { query: this.payment.payerAccount.customer.idNumber },
          true
        );
      this.initializeFieldsState();
    }
  }

  @Input()
  public mode: { isOn: boolean; edit: boolean };

  @Output()
  public onHide: EventEmitter<boolean> = new EventEmitter<boolean>();

  maxDate: Date;

  @Input()
  payment: PaymentModel;

  accountList: AccountModel[];

  invoiceCoverageList: InvoiceCoverageModel[];

  fieldStates: {
    payer: FieldState;
    amount: FieldState;
    paymentDate: FieldState;
    invoices: FieldState;
  };

  get field() {
    return this.fieldStates;
  }

  filterAccount(event, init: boolean = false) {
    this.paymentService.searchAccounts(event.query).subscribe((d) => {
      this.accountList = d.items;
      if (init) {
        this.payment.payerAccount = this.accountList.filter(
          (a) =>
            a.customer.idNumber == this.payment.payerAccount.customer.idNumber
        )[0];
        this.filterInvoice({ query: '' }, true);
      }
    });
  }

  filterInvoice(event, init: boolean = false) {
    this.paymentService
      .searchInvoices(event.query, this.payment.payerAccount.accountId, !init)
      .subscribe((d) => {
        let invoices = d.items;
        this.invoiceCoverageList = invoices.map((c) => {
          let t = new InvoiceCoverageModel();
          t.invoice = c;
          t.amount = 0;
          return t;
        });
        if (this.mode.edit && init) {
          if (this.payment.activeInvoiceCoverageCodes)
            this.payment.activeInvoiceCoverages =
              this.invoiceCoverageList.filter(
                (c) =>
                  this.payment.activeInvoiceCoverageCodes.indexOf(
                    c.invoice.code
                  ) > -1
              );
          else this.payment.activeInvoiceCoverages = [];
        }
      });
  }

  onPayerSelect(newPayer: AccountModel) {
    this.payment.activeInvoiceCoverages = this.payment.activeInvoiceCoverages
      .filter((c) => c.invoice.payerAccount.accountId == newPayer.accountId)
      .sort((a, b) =>
        a.createDate > b.createDate ? 1 : a.createDate < b.createDate ? -1 : 0
      );

    this.recalculateLeftAmount();
    this.validate();
  }

  recalculateLeftAmount() {
    let payment = this.payment;
    payment.amountLeft = payment.amount;
    let coverages = payment.activeInvoiceCoverages;
    let invoices = coverages.map((c) => c.invoice);

    for (let i = 0; i < coverages.length; i++) {
      let payable = Math.min(
        payment.amountLeft,
        this.mode.edit && invoices[i].invoiceCoverages.length > 0
          ? invoices[i].invoiceCoverages[0].amount
          : invoices[i].amountLeft
      );
      coverages[i].amount = payable;
      payment.amountLeft -= payable;
    }
  }

  validate() {
    let validators = this.getValidators();
    this.invokeValidation(validators);
  }

  invokeValidation(validators: IValidator[], onValid?: () => void) {
    ValidationUtils.invokeValidation(
      validators,
      this.getFieldStatesObject(),
      onValid
    );
  }

  getFieldStatesObject() {
    let result: { [index: string]: FieldState } = {};
    for (let p in this.fieldStates) {
      if (this.fieldStates.hasOwnProperty(p)) {
        result[this.fieldStates[p].fieldId] = this.fieldStates[p];
      }
    }

    return result;
  }

  getValidators() {
    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );
    let incorrectFormat = this.translationService.instant(
      'message.IncorrectFormat'
    );
    let uncoveredInvoicesMessage = <string>(
      this.translationService.instant('message.UncoveredInvoicesBoundToPayment')
    );
    let field = this.field;
    let payment = this.payment;

    let uncoveredInvoices = payment.activeInvoiceCoverages.filter(
      (i) => i.amount == 0
    );
    let uncoveredInvoiceCodes = uncoveredInvoices.reduce(
      (a, b) => a + b + ', ',
      ''
    );
    if (uncoveredInvoiceCodes.length > 0) {
      uncoveredInvoiceCodes = uncoveredInvoiceCodes.substring(
        0,
        uncoveredInvoiceCodes.length - 2
      );
    }

    uncoveredInvoicesMessage = uncoveredInvoicesMessage.replace(
      '{0}',
      uncoveredInvoiceCodes
    );

    return [
      Validator.forRequired(
        field.payer,
        () => payment.payerAccount && payment.payerAccount.accountId,
        emptyMessage
      ),
      Validator.forRequired(field.amount, () => payment.amount, emptyMessage),
      Validator.forRequired(
        field.paymentDate,
        () => payment.paymentDate,
        emptyMessage
      ),
      Validator.forPredicate(
        field.paymentDate,
        () => payment.paymentDate <= new Date(),
        incorrectFormat
      ),
      Validator.forPredicate(
        field.invoices,
        () => uncoveredInvoices.length == 0,
        uncoveredInvoicesMessage
      ),
    ];
  }

  onInvoiceSelect(newInvoice) {
    this.payment.activeInvoiceCoverages =
      this.payment.activeInvoiceCoverages.sort((a, b) =>
        a.createDate > b.createDate ? 1 : a.createDate < b.createDate ? -1 : 0
      );

    this.recalculateLeftAmount();
    this.validate();
  }

  onInvoiceUnselect(removedInvoice) {
    this.payment.activeInvoiceCoverages =
      this.payment.activeInvoiceCoverages.sort((a, b) =>
        a.createDate > b.createDate ? 1 : a.createDate < b.createDate ? -1 : 0
      );

    // for this moment, removedItem isn't removed from activeInvoiceCoverages list and if we don't remove it manualy, recalculation will work wrong
    this.payment.activeInvoiceCoverages.splice(
      this.payment.activeInvoiceCoverages.indexOf(removedInvoice),
      1
    );
    this.recalculateLeftAmount();
    this.validate();
  }

  onAmountChange() {
    // this.recalculateLeftAmount();
    this.validate();
  }

  onSave() {
    this.recalculateLeftAmount();
    this.invokeValidation(this.getValidators(), () => {
      if (this.mode.edit) {
        this.paymentService.editPayment(this.payment).subscribe((d) => {
          this.onHide.emit(true);
        });
      } else {
        this.paymentService.addPayment(this.payment).subscribe((d) => {
          this.onHide.emit(true);
        });
      }
    });
  }

  onClose() {
    this.onHide.emit(false);
  }

  private initializeFieldsState() {
    this.fieldStates = {
      payer: new FieldState(() => this.payment.payerAccount),
      amount: new FieldState(() => this.payment.amount),
      paymentDate: new FieldState(() => this.payment.paymentDate),
      invoices: new FieldState(() => this.payment.activeInvoiceCoverages),
    };
  }

  ngOnInit() {}
}
